export const WEBPROFIT_RESPONSIVE = Object.freeze({
  contentMaxWidth: 1920,
  touchTarget: 44,
  mobileGutter: 12,
  tabletGutter: 20,
  desktopGutter: 24,
  safeTop: "env(safe-area-inset-top, 0px)",
  safeRight: "env(safe-area-inset-right, 0px)",
  safeBottom: "env(safe-area-inset-bottom, 0px)",
  safeLeft: "env(safe-area-inset-left, 0px)",
});

export const WEBPROFIT_MOBILE_TYPOGRAPHY = {
  h1: {
    "@media (max-width:599.95px)": { fontSize: "2rem", lineHeight: 1.08, letterSpacing: "-0.02em" },
  },
  h2: {
    "@media (max-width:599.95px)": { fontSize: "1.7rem", lineHeight: 1.1, letterSpacing: "-0.015em" },
  },
  h3: {
    "@media (max-width:599.95px)": { fontSize: "1.45rem", lineHeight: 1.12 },
  },
  h4: {
    "@media (max-width:599.95px)": { fontSize: "1.22rem", lineHeight: 1.16 },
  },
  h5: {
    "@media (max-width:599.95px)": { fontSize: "1.05rem", lineHeight: 1.18 },
  },
  h6: {
    "@media (max-width:599.95px)": { fontSize: ".98rem", lineHeight: 1.2 },
  },
  body1: {
    "@media (max-width:599.95px)": { fontSize: ".9rem", lineHeight: 1.48 },
  },
  body2: {
    "@media (max-width:599.95px)": { fontSize: ".8rem", lineHeight: 1.42 },
  },
};

export const dashboardContentContainerSx = {
  width: "100%",
  maxWidth: WEBPROFIT_RESPONSIVE.contentMaxWidth,
  mx: "auto",
  px: {
    xs: `${WEBPROFIT_RESPONSIVE.mobileGutter}px`,
    sm: `${WEBPROFIT_RESPONSIVE.tabletGutter}px`,
    lg: `${WEBPROFIT_RESPONSIVE.desktopGutter}px`,
  },
};

export const dashboardFormScopeSx = {
  minWidth: 0,
  "& .MuiFormControl-root, & .MuiTextField-root": { minWidth: 0, maxWidth: "100%" },
  "& .MuiInputBase-root": { minWidth: 0, maxWidth: "100%" },
  "& .MuiInputBase-input, & .MuiSelect-select": { minWidth: 0 },
  "& .MuiFormGroup-root": { minWidth: 0, maxWidth: "100%" },
  "& .MuiFormHelperText-root": { overflowWrap: "anywhere" },
};

export const dashboardMobileScopeSx = {
  minWidth: 0,
  "& .MuiPaper-root, & .MuiCard-root": { borderRadius: { xs: "14px", sm: "16px" } },
  "& .MuiCardContent-root": { p: { xs: "14px", sm: "18px" } },
  "& .MuiCardContent-root:last-child": { pb: { xs: "14px", sm: "18px" } },
  "& .MuiTableContainer-root": {
    maxWidth: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  "& .MuiTableCell-root": { px: { xs: 1, sm: 1.5 }, py: { xs: .9, sm: 1.05 } },
  "& .MuiAlert-root": { py: { xs: .7, sm: .9 }, px: { xs: 1.15, sm: 1.5 } },
  "& img, & video, & canvas, & svg": { maxWidth: "100%" },
  "& pre": { maxWidth: "100%", overflowX: "auto" },
};

export const responsiveActionsSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  gap: { xs: 1, sm: 1 },
  width: { xs: "100%", sm: "auto" },
  "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
};

export const responsiveFormGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
  gap: { xs: 1.25, sm: 1.75 },
  minWidth: 0,
  "& > *": { minWidth: 0 },
};

export const responsiveCardSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  borderRadius: { xs: 2.5, sm: 3.5 },
};

export const responsiveBottomDrawerPaperSx = {
  width: "100%",
  maxWidth: { sm: 760 },
  mx: "auto",
  maxHeight: { xs: "74dvh", sm: "62dvh" },
  borderRadius: "22px 22px 0 0",
  border: "1px solid rgba(25,36,47,.08)",
  borderBottom: 0,
  overflow: "hidden",
};

export const denseDashboardContentSx = {
  minWidth: 0,
  overflow: "hidden",
  "& .MuiFormHelperText-root": { mt: .45, fontSize: { xs: ".69rem", sm: ".73rem" }, lineHeight: 1.3 },
  "& .MuiTextField-root, & .MuiFormControl-root": { maxWidth: "100%" },
  "& .MuiInputBase-root": { fontSize: { xs: ".86rem", sm: ".9rem" } },
  "& .MuiGrid-container": { minWidth: 0 },
  "& .MuiGrid-item": { minWidth: 0 },
};

export const compactNavigationToolButtonSx = {
  minHeight: WEBPROFIT_RESPONSIVE.touchTarget,
  px: 1.2,
  borderRadius: 2,
  textTransform: "none",
  fontSize: ".75rem",
};

export const webProfitResponsiveComponents = {
  MuiContainer: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ownerState?.disableGutters ? {} : ({
        [theme.breakpoints.down("sm")]: {
          paddingLeft: WEBPROFIT_RESPONSIVE.mobileGutter,
          paddingRight: WEBPROFIT_RESPONSIVE.mobileGutter,
        },
        [theme.breakpoints.between("sm", "lg")]: {
          paddingLeft: WEBPROFIT_RESPONSIVE.tabletGutter,
          paddingRight: WEBPROFIT_RESPONSIVE.tabletGutter,
        },
        [theme.breakpoints.up("lg")]: {
          paddingLeft: WEBPROFIT_RESPONSIVE.desktopGutter,
          paddingRight: WEBPROFIT_RESPONSIVE.desktopGutter,
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          minHeight: WEBPROFIT_RESPONSIVE.touchTarget,
          maxWidth: "100%",
        },
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          minWidth: WEBPROFIT_RESPONSIVE.touchTarget,
          minHeight: WEBPROFIT_RESPONSIVE.touchTarget,
        },
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: { minHeight: WEBPROFIT_RESPONSIVE.touchTarget },
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: { minHeight: WEBPROFIT_RESPONSIVE.touchTarget },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        maxWidth: "100%",
        [theme.breakpoints.down("sm")]: {
          "&.MuiChip-clickable": { minHeight: WEBPROFIT_RESPONSIVE.touchTarget },
        },
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        maxWidth: "100%",
        [theme.breakpoints.down("sm")]: {
          minHeight: WEBPROFIT_RESPONSIVE.touchTarget,
          fontSize: ".9rem",
          "&.MuiInputBase-multiline": { minHeight: "auto" },
        },
      }),
      input: {
        minWidth: 0,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: { maxWidth: "100%", minWidth: 0 },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: { fontSize: ".7rem", lineHeight: 1.3 },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 0,
        maxWidth: "100%",
        [theme.breakpoints.down("sm")]: { borderRadius: 14 },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          padding: 14,
          "&:last-child": { paddingBottom: 14 },
        },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          margin: 12,
          width: "calc(100% - 24px)",
          maxHeight: `calc(100dvh - 24px - ${WEBPROFIT_RESPONSIVE.safeTop} - ${WEBPROFIT_RESPONSIVE.safeBottom})`,
          borderRadius: 16,
          "&.MuiDialog-paperFullScreen": {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            maxHeight: "none",
            borderRadius: 0,
          },
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        overflowWrap: "anywhere",
        [theme.breakpoints.down("sm")]: { padding: "16px 16px 12px" },
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 0,
        [theme.breakpoints.down("sm")]: { paddingLeft: 16, paddingRight: 16 },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          gap: 8,
          padding: 16,
          flexWrap: "wrap",
          "& .MuiButton-root": { flex: "1 1 160px", marginLeft: "0 !important" },
        },
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        maxWidth: "100vw",
        overscrollBehavior: "contain",
        [theme.breakpoints.down("sm")]: { WebkitOverflowScrolling: "touch" },
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        maxWidth: "calc(100vw - 24px)",
        [theme.breakpoints.down("sm")]: {
          maxHeight: `calc(100dvh - 24px - ${WEBPROFIT_RESPONSIVE.safeTop} - ${WEBPROFIT_RESPONSIVE.safeBottom})`,
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          maxWidth: "calc(100vw - 24px)",
          maxHeight: `calc(100dvh - 24px - ${WEBPROFIT_RESPONSIVE.safeTop} - ${WEBPROFIT_RESPONSIVE.safeBottom})`,
        },
      }),
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        maxWidth: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      toolbar: ({ theme }) => ({
        minHeight: WEBPROFIT_RESPONSIVE.touchTarget,
        [theme.breakpoints.down("sm")]: {
          minHeight: 52,
          paddingLeft: 8,
          paddingRight: 4,
          gap: 4,
          flexWrap: "nowrap",
          "& .MuiTablePagination-spacer": { display: "none" },
          "& .MuiTablePagination-selectLabel": { display: "none" },
          "& .MuiTablePagination-select": { paddingRight: 22 },
          "& .MuiTablePagination-displayedRows": {
            marginLeft: "auto",
            whiteSpace: "nowrap",
            fontSize: ".74rem",
          },
          "& .MuiTablePagination-actions": {
            marginLeft: 0,
            display: "flex",
          },
        },
      }),
    },
  },
  MuiPagination: {
    defaultProps: {
      siblingCount: 0,
      boundaryCount: 1,
    },
    styleOverrides: {
      ul: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: {
          flexWrap: "nowrap",
          justifyContent: "center",
          "& .MuiPaginationItem-root": {
            minWidth: 36,
            height: 36,
            margin: "0 2px",
          },
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { maxWidth: "100%" },
      scroller: { WebkitOverflowScrolling: "touch" },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down("sm")]: { minHeight: WEBPROFIT_RESPONSIVE.touchTarget },
      }),
    },
  },
};
